import type { LetterPickerErrorData } from '../components/learning/LetterPicker';

interface WordResult {
    word: string;
    userInput: string;
    isCorrect: boolean;
}

// ---------------------------------------------------------------------------
// Levenshtein edit-distance alignment
// ---------------------------------------------------------------------------

/**
 * Compute optimal alignment between two words using Levenshtein DP.
 * Returns only substitution edits — these are the meaningful letter-pair
 * confusions for phonics analysis.
 *
 * Deletions/insertions are NOT returned as errors because they don't produce
 * a valid (expected, attempted) pair for phonics classification.
 */
function alignSubstitutions(
    expected: string,
    typed: string,
): Array<{ position: number; expected: string; attempted: string }> {
    const m = expected.length;
    const n = typed.length;

    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;

    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (expected[i - 1] === typed[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1];
            } else {
                dp[i][j] = 1 + Math.min(
                    dp[i - 1][j],     // delete from expected
                    dp[i][j - 1],     // insert
                    dp[i - 1][j - 1], // substitute
                );
            }
        }
    }

    // Backtrack — only collect substitutions
    const subs: Array<{ position: number; expected: string; attempted: string }> = [];
    let i = m;
    let j = n;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && expected[i - 1] === typed[j - 1]) {
            i--; j--;
        } else if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
            subs.push({ position: i - 1, expected: expected[i - 1], attempted: typed[j - 1] });
            i--; j--;
        } else if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
            // deletion — skip (no valid pair)
            i--;
        } else {
            // insertion — skip
            j--;
        }
    }

    return subs.reverse();
}

/**
 * Compute edit distance between two strings.
 * Used to filter out very dissimilar word pairs that don't represent
 * meaningful phonics errors (e.g. once → what).
 */
function editDistance(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (a[i - 1] === b[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1];
            } else {
                dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
            }
        }
    }
    return dp[m][n];
}

/** Minimum similarity ratio to consider a word pair for phonics analysis */
const MIN_SIMILARITY = 0.4;

/**
 * Convert typing-mode WordResult[] into LetterPickerErrorData
 * so that phonics error analysis and prosody detection can reuse
 * the same pipeline as LetterPicker mode.
 *
 * Uses Levenshtein edit-distance alignment to correctly identify
 * substitutions vs insertions/deletions. Only substitutions produce
 * phonics error entries — insertions and deletions are skipped.
 *
 * Very dissimilar word pairs (similarity < 40%) are skipped entirely
 * since they represent mishearing the whole word, not phonics confusion.
 */
export function buildErrorDataFromWordResults(
    expectedSentence: string,
    wordResults: WordResult[],
): LetterPickerErrorData {
    const errors: LetterPickerErrorData['errors'] = [];
    const expectedLetters: string[] = [];
    let totalLetters = 0;

    // Build a map of word index → character offset in the full sentence
    const regex = /\S+/g;
    let match: RegExpExecArray | null;
    const wordOffsets: number[] = [];
    while ((match = regex.exec(expectedSentence)) !== null) {
        wordOffsets.push(match.index);
    }

    for (let wi = 0; wi < wordResults.length; wi++) {
        const wr = wordResults[wi];
        const expected = wr.word.toLowerCase();
        const typed = wr.userInput.toLowerCase();
        const baseOffset = wordOffsets[wi] ?? 0;

        // Collect expected letters for this word
        for (let i = 0; i < expected.length; i++) {
            const ch = expected[i];
            if (/[a-z]/i.test(ch)) {
                expectedLetters.push(ch);
                totalLetters++;
            }
        }

        if (wr.isCorrect) continue;

        // Skip very dissimilar word pairs — they're whole-word mishearings,
        // not phonics-level confusions (e.g. once → what, pressure → question)
        const maxLen = Math.max(expected.length, typed.length);
        if (maxLen > 0) {
            const dist = editDistance(expected, typed);
            const similarity = 1 - dist / maxLen;
            if (similarity < MIN_SIMILARITY) continue;
        }

        // Use edit-distance alignment — only substitutions become errors
        const subs = alignSubstitutions(expected, typed);
        for (const sub of subs) {
            errors.push({
                position: baseOffset + sub.position,
                expected: sub.expected,
                attempted: [sub.attempted],
            });
        }
    }

    return {
        expectedText: expectedSentence,
        errors,
        revealedPositions: [], // typing mode has no hints
        totalLetters,
        expectedLetters,
    };
}
